import { Box, Typography, Card, Divider, Link, useMediaQuery, Theme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useRecordContext } from "react-admin";
import AnimatedBlock from "../../../components/Common/AnimatedBlock";
import InfoRow from "../../../components/Common/InfoRow";

const PLAIN_EMAIL = /^[^\s@?&#%]+@[^\s@?&#%]+\.[^\s@?&#%]+$/;

// The API stores a field left empty as "": both read as not provided.
const given = (value?: string) => (typeof value === "string" && value.trim() ? value.trim() : null);

const Section = ({ title, children }: { title: string; children: any }) => (
  <>
    <Box px={2} pt={2}>
      <Typography variant="overline" color="text.secondary">
        {title}
      </Typography>
    </Box>
    <Box px={2} pb={1}>
      {children}
    </Box>
  </>
);

// Who the link is for and, when it was emailed, who sent it and with what message.
const CustomerTab = () => {
  const { t }: { t: any } = useTranslation();
  const record = useRecordContext();
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down("sm"));
  if (!record) return null;

  const params = record.params || {};
  const none = <Typography component="span" variant="body2" color="text.secondary">{t("Not provided")}</Typography>;
  // Only a plain address becomes a link: a value with a query (?bcc=, &body=) stays text.
  const email = (value: string | null) =>
    !value ? none : PLAIN_EMAIL.test(value) ? <Link href={`mailto:${value}`} underline="hover">{value}</Link> : value;
  const sender = given(params.senderName) || given(params.senderCompanyName) || given(params.senderEmail);
  const message = given(params.message);

  return (
    <Box>
      {!isMobile && (
        <Box pb={2}>
          <Typography variant="h6">
            {t("Customer")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("Who the link is for and who sent it")}
          </Typography>
        </Box>
      )}

      <AnimatedBlock>
        <Card>
          <Section title={t("Customer")}>
            <InfoRow label={t("Name")}>{given(params.customerName) || none}</InfoRow>
            <InfoRow label={t("Email")}>{email(given(params.customerEmail))}</InfoRow>
          </Section>
          {sender && (
            <>
              <Divider />
              <Section title={t("Sent by")}>
                <InfoRow label={t("Name")}>{given(params.senderName) || none}</InfoRow>
                <InfoRow label={t("Company")}>{given(params.senderCompanyName) || none}</InfoRow>
                <InfoRow label={t("Email")}>{email(given(params.senderEmail))}</InfoRow>
              </Section>
            </>
          )}
          {message && (
            <>
              <Divider />
              <Section title={t("Message")}>
                <Typography variant="body2" sx={{ py: 1, whiteSpace: "pre-line", overflowWrap: "anywhere" }}>
                  {message}
                </Typography>
              </Section>
            </>
          )}
        </Card>
      </AnimatedBlock>
    </Box>
  );
};

export default CustomerTab;
